import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { Dropdown, MenuProps, message } from 'antd';
import { FollowIcon, UnfollowIcon, VolumeIcon } from '../Icons';

// Utils
import { useTranslation } from 'react-i18next';

// Interface
import type { Artist, SimpleArtist } from '../../interfaces/artist';

// Redux
import { useAppDispatch, useAppSelector } from '../../store/store';
import { userService } from '../../services/users';
import { yourLibraryActions } from '../../store/slices/yourLibrary';
import { uiActions } from '../../store/slices/ui';
import { useMutedEntity } from '../../hooks/useMutedEntity';
import type { MutedEntityType } from '../../services/mutes';
import { DropdownSubPanel } from './DropdownSubPanel';
import { EntityPreferenceMenu } from './EntityPreferenceMenu';
import { useDropdownSubPanel } from './useDropdownSubPanel';
import {
  fetchPreferences,
  preferencesSelectors,
  removePreference,
  upsertPreference,
} from '../../store/slices/preferences';

const formatPreferenceLabel = (value: number) => {
  const formatted = value.toFixed(2).replace(/\.?0+$/, '');
  return `${formatted}x`;
};

const clampPreferenceWeight = (value: number): number => {
  if (!Number.isFinite(value)) return 1;
  return value;
};

interface ArtistActionsWrapperProps {
  artist: Artist | Spotify.Track['artists'][0] | SimpleArtist;
  onRefresh?: () => void;
  trigger?: ('contextMenu' | 'click')[];
  children: ReactNode | ReactNode[];
  entityType?: MutedEntityType;
  entityIdOverride?: string | null;
  enablePreferenceMenu?: boolean;
}

export const ArtistActionsWrapper: FC<ArtistActionsWrapperProps> = memo((props) => {
  const {
    children,
    artist,
    entityType = 'artist',
    onRefresh,
    trigger,
    entityIdOverride,
    enablePreferenceMenu = true
  } = props;

  const { t } = useTranslation(['playlist']);

  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => !!state.auth.user);
  const myArtists = useAppSelector((state) => state.yourLibrary.myArtists);

  const entityId = useMemo(() => {
    if (typeof entityIdOverride === 'string' && entityIdOverride.trim().length > 0) {
      return entityIdOverride.trim();
    }
    // @ts-ignore
    const explicitId = typeof artist.id === 'string' ? artist.id : null;
    if (explicitId) return explicitId;
    // @ts-ignore
    const uri = typeof artist.uri === 'string' ? artist.uri : null;
    if (uri) {
      const parts = uri.split(':');
      const last = parts[parts.length - 1];
      if (last) {
        return last;
      }
    }
    return null;
  }, [artist, entityIdOverride]);

  const preferenceEntityType = entityType === 'composer' ? 'composer' : 'artist';

  const { isMuted, pending, toggle } = useMutedEntity(
    entityType,
    entityId ?? undefined,
    {
      label: typeof (artist as Artist)?.name === 'string' ? (artist as Artist).name : entityId ?? 'artist',
      initialSource: entityType === 'composer' ? 'composer-menu' : 'artist-menu'
    }
  );

  const prefPanel = useDropdownSubPanel();
  const prefPanelHoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prefPanelOpenedViaClickRef = useRef(false);
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const dropdownContentRef = useRef<HTMLDivElement | null>(null);
  const suppressDropdownCloseRef = useRef(false);
  const suppressDropdownCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [preferenceDraft, setPreferenceDraft] = useState(1);
  const [preferenceSaving, setPreferenceSaving] = useState(false);
  const isAnyPanelOpen = prefPanel.isOpen || prefPanelOpenedViaClickRef.current;

  const preferencesStatus = useAppSelector(preferencesSelectors.selectPreferencesStatus);
  const shouldRefreshPreferences = useAppSelector(
    preferencesSelectors.selectPreferencesShouldRefresh
  );
  const existingPreference = useAppSelector((state) =>
    entityId
      ? preferencesSelectors.selectPreference(state, preferenceEntityType, entityId)
      : undefined
  );

  useEffect(() => {
    if (!enablePreferenceMenu) return;
    if (!entityId) return;
    if (preferencesStatus === 'loading') return;
    if (preferencesStatus === 'idle' || shouldRefreshPreferences) {
      dispatch(fetchPreferences()).catch(() => {});
    }
  }, [dispatch, enablePreferenceMenu, entityId, preferencesStatus, shouldRefreshPreferences]);

  useEffect(() => {
    if (!enablePreferenceMenu) return;
    setPreferenceDraft(clampPreferenceWeight(existingPreference?.weight ?? 1));
  }, [enablePreferenceMenu, existingPreference?.weight, entityId]);

  useEffect(() => {
    return () => {
      if (suppressDropdownCloseTimeoutRef.current) {
        clearTimeout(suppressDropdownCloseTimeoutRef.current);
        suppressDropdownCloseTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!enablePreferenceMenu) return;
    // Keep the dropdown open whenever the preference subpanel is open.
    if (prefPanel.isOpen && !menuOpen) {
      setMenuOpen(true);
    }
  }, [enablePreferenceMenu, prefPanel.isOpen, menuOpen]);

  const markSuppressDropdownClose = useCallback(() => {
    suppressDropdownCloseRef.current = true;
    if (suppressDropdownCloseTimeoutRef.current) {
      clearTimeout(suppressDropdownCloseTimeoutRef.current);
    }
    suppressDropdownCloseTimeoutRef.current = setTimeout(() => {
      suppressDropdownCloseRef.current = false;
      suppressDropdownCloseTimeoutRef.current = null;
    }, 250);
  }, []);

  const clearSuppressDropdownCloseTimeout = useCallback(() => {
    if (suppressDropdownCloseTimeoutRef.current) {
      clearTimeout(suppressDropdownCloseTimeoutRef.current);
      suppressDropdownCloseTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (prefPanelHoverTimeoutRef.current) {
        clearTimeout(prefPanelHoverTimeoutRef.current);
        prefPanelHoverTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!enablePreferenceMenu) return;
    if (menuOpen) return;
    prefPanelOpenedViaClickRef.current = false;
    setPreferenceDraft(clampPreferenceWeight(existingPreference?.weight ?? 1));
    prefPanel.close();
    if (openKeys.length) {
      setOpenKeys([]);
    }
  }, [enablePreferenceMenu, menuOpen, openKeys.length, prefPanel, existingPreference?.weight]);

  const handleUserValidation = useCallback(() => {
    if (!user) {
      dispatch(uiActions.openLoginTooltip());
      return false;
    }
    return true;
  }, [dispatch, user]);

  const inLibrary = useMemo(() => {
    if (!entityId) return false;
    return myArtists.some((p) => p.id === entityId);
  }, [myArtists, entityId]);

  const handlePreferenceApply = useCallback(async () => {
    if (!entityId) return;
    if (!handleUserValidation()) return;
    const normalized =
      typeof preferenceDraft === 'number' && Number.isFinite(preferenceDraft)
        ? preferenceDraft
        : 1;
    const clamped = clampPreferenceWeight(normalized);
    setPreferenceDraft(clamped);
    setPreferenceSaving(true);
    try {
      if (Math.abs(clamped - 1) < 0.0001) {
        await dispatch(
          removePreference({
            entityType: preferenceEntityType,
            entityId
          })
        ).unwrap();
        setPreferenceDraft(1);
        message.success(
          `${preferenceEntityType === 'composer' ? 'Composer' : 'Artist'} preference reset to neutral.`
        );
        prefPanel.close();
        prefPanelOpenedViaClickRef.current = false;
        setMenuOpen(false);
        return;
      }
      const saved = await dispatch(
        upsertPreference({
          entityType: preferenceEntityType,
          entityId,
          weight: clamped
        })
      ).unwrap();
      setPreferenceDraft(saved?.weight ?? clamped);
      message.success(
        `${preferenceEntityType === 'composer' ? 'Composer' : 'Artist'} preference saved.`
      );
      prefPanel.close();
      prefPanelOpenedViaClickRef.current = false;
      setMenuOpen(false);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('[ArtistActions] failed to save preference', { entityId, error });
      }
      message.error('Unable to save preference right now.');
    } finally {
      setPreferenceSaving(false);
    }
  }, [dispatch, entityId, handleUserValidation, preferenceDraft, preferenceEntityType, prefPanel]);

  const handlePreferenceCancel = useCallback(() => {
    if (existingPreference?.weight != null) {
      setPreferenceDraft(clampPreferenceWeight(existingPreference.weight));
    } else {
      setPreferenceDraft(1);
    }
    prefPanel.close();
  }, [existingPreference?.weight, prefPanel]);

  const togglePreferencePanel = useCallback(() => {
    if (!enablePreferenceMenu) return;
    markSuppressDropdownClose();
    prefPanelOpenedViaClickRef.current = true;
    if (prefPanel.isOpen) {
      prefPanel.close();
      setMenuOpen(true);
      return;
    }
    setMenuOpen(true);
    prefPanel.open();
  }, [enablePreferenceMenu, prefPanel, markSuppressDropdownClose]);

  const handleMenuOpenChange = useCallback(
    (open: boolean) => {
      setMenuOpen(open);
      if (!open) {
        prefPanel.close();
        prefPanelOpenedViaClickRef.current = false;
        if (prefPanelHoverTimeoutRef.current) {
          clearTimeout(prefPanelHoverTimeoutRef.current);
          prefPanelHoverTimeoutRef.current = null;
        }
        setOpenKeys([]);
      }
    },
    [prefPanel]
  );

  const renderDropdown = useCallback(
    (originNode: ReactNode) => {
      if (!originNode) {
        return originNode;
      }
      return (
        <div ref={dropdownContentRef} className='artist-actions-dropdown__content'>
          {originNode}
        </div>
      );
    },
    []
  );

  const items = useMemo(() => {
    const items: MenuProps['items'] = [];
    if (!entityId) return items;

    if (inLibrary) {
      items.push({
        key: 'remove',
        label: t('Unfollow'),
        icon: <UnfollowIcon />,
        onClick: async () => {
          if (!handleUserValidation()) return;
          userService.unfollowArtists([entityId]).then(() => {
            message.success(t('Artist unfollowed'));
            dispatch(yourLibraryActions.fetchMyArtists());
          });
        },
      });
    } else {
      items.push({
        key: 'add',
        label: t('Follow'),
        icon: <FollowIcon />,
        onClick: async () => {
          if (!handleUserValidation()) return;
          userService.followArtists([entityId]).then(() => {
            message.success(t('Artist followed'));
            dispatch(yourLibraryActions.fetchMyArtists());
          });
        },
      });
    }

    // Mute action is handled inside the preference panel; omit here to avoid duplication.

    const preferenceLabelWeight = formatPreferenceLabel(preferenceDraft);
    if (enablePreferenceMenu) {
      items.push({
        key: 'preference',
        label: (
          <div
            ref={(el) => {
              prefPanel.triggerRef.current = el;
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const nativeEvent = e.nativeEvent as MouseEvent & {
                stopImmediatePropagation?: () => void;
              };
              nativeEvent.stopImmediatePropagation?.();
              markSuppressDropdownClose();
              togglePreferencePanel();
            }}
            style={{ cursor: 'pointer' }}
          >
            {`Adjust ${preferenceEntityType === 'composer' ? 'composer' : 'artist'} freq (${preferenceLabelWeight})`}
          </div>
        ),
        onClick: () => {
          togglePreferencePanel();
        },
        icon: (
          <div
            style={{
              width: 16,
              height: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <VolumeIcon />
          </div>
        ),
        children: [
          {
            key: 'preference-dummy',
            label: '',
            style: { display: 'none', height: 0, padding: 0, margin: 0 }
          }
        ],
        popupClassName: 'cascade-menu__popup',
        popupStyle: { display: 'none' },
      });
    }

    return items;
  }, [
    dispatch,
    entityId,
    handleUserValidation,
    inLibrary,
    isMuted,
    pending,
    toggle,
    entityType,
    onRefresh,
    t,
    prefPanel,
    preferenceDraft,
    preferenceEntityType,
    enablePreferenceMenu,
    togglePreferencePanel,
    markSuppressDropdownClose
  ]);

  useEffect(() => {
    if (!menuOpen) return;

    const closeMenu = () => {
      setMenuOpen(false);
      prefPanel.close();
      prefPanelOpenedViaClickRef.current = false;
      setOpenKeys([]);
    };

    const handlePointerLikeEvent = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (triggerRef.current?.contains(target)) return;
      if (dropdownContentRef.current?.contains(target)) return;
      if (target.closest('.artist-actions-dropdown')) return;
      if (target.closest('.cascade-menu__popup')) return;
      if (
        target.closest('.dropdown-subpanel-wrapper') !== null ||
        target.closest('.entity-preference-menu') !== null ||
        target.closest('.cascade-menu') !== null ||
        target.closest('.ant-dropdown-menu-submenu-popup') !== null ||
        target.closest('.ant-dropdown-menu-sub') !== null
      ) {
        return;
      }
      closeMenu();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    const listenerOptions: AddEventListenerOptions = { capture: true };
    const pointerEvents =
      typeof window !== 'undefined' && 'PointerEvent' in window
        ? ['pointerdown']
        : ['mousedown', 'touchstart'];

    const eventTargets: Array<[string, (event: Event) => void]> = pointerEvents.map((name) => [
      name,
      handlePointerLikeEvent,
    ]);
    eventTargets.push(['click', handlePointerLikeEvent]);

    eventTargets.forEach(([name, handler]) =>
      document.addEventListener(name, handler, listenerOptions)
    );
    document.addEventListener('keydown', handleKeyDown, listenerOptions);

    return () => {
      eventTargets.forEach(([name, handler]) =>
        document.removeEventListener(name, handler, listenerOptions)
      );
      document.removeEventListener('keydown', handleKeyDown, listenerOptions);
    };
  }, [menuOpen, prefPanel]);

  return (
    <>
      <Dropdown
        trigger={trigger}
        menu={{
          items,
          triggerSubMenuAction: 'click',
          openKeys: openKeys.filter((key) => key !== 'preference'),
          onOpenChange: (keys) => {
            const realKeys = keys.filter((key) => key !== 'preference');
            setOpenKeys(realKeys);
          }
        }}
        overlayClassName='artist-actions-dropdown'
        destroyPopupOnHide
        getPopupContainer={() => document.body}
        dropdownRender={renderDropdown}
        open={menuOpen || prefPanel.isOpen}
        onOpenChange={(open) => {
          if (
            !open &&
            (isAnyPanelOpen || prefPanel.isOpen || suppressDropdownCloseRef.current || prefPanelOpenedViaClickRef.current)
            ) {
            // Ignore the close request if a subpanel is open or we're in the middle of opening one.
            clearSuppressDropdownCloseTimeout();
            suppressDropdownCloseRef.current = false;
            prefPanelOpenedViaClickRef.current = false;
            setMenuOpen(true);
            return;
          }
          clearSuppressDropdownCloseTimeout();
          suppressDropdownCloseRef.current = false;
          handleMenuOpenChange(open);
          if (!open) {
            setOpenKeys([]);
          }
        }}
      >
        <span ref={triggerRef}>{children}</span>
      </Dropdown>
      {prefPanel.isOpen && (
        <DropdownSubPanel
          isOpen={prefPanel.isOpen}
          zIndex={9999}
          onClose={() => {
            prefPanel.close();
            prefPanelOpenedViaClickRef.current = false;
            setOpenKeys([]);
          }}
          triggerElement={prefPanel.triggerRef.current as HTMLElement | null}
          parentDropdownOpen={menuOpen}
          onParentDropdownChange={handleMenuOpenChange}
          onMouseEnter={() => {
            if (prefPanelHoverTimeoutRef.current) {
              clearTimeout(prefPanelHoverTimeoutRef.current);
              prefPanelHoverTimeoutRef.current = null;
            }
          }}
          onMouseLeave={() => {
            prefPanelHoverTimeoutRef.current = setTimeout(() => {
              prefPanel.close();
              prefPanelOpenedViaClickRef.current = false;
              prefPanelHoverTimeoutRef.current = null;
            }, 150);
          }}
        >
          <EntityPreferenceMenu
            entityType={preferenceEntityType}
            value={preferenceDraft}
            disabled={preferenceSaving}
            isMuted={isMuted}
            mutePending={pending}
            onToggleMute={() => {
              void toggle({
                source: entityType === 'composer' ? 'composer-menu' : 'artist-menu'
              });
            }}
            onChange={(next) => setPreferenceDraft(clampPreferenceWeight(next))}
            onApply={handlePreferenceApply}
            onCancel={handlePreferenceCancel}
          />
        </DropdownSubPanel>
      )}
    </>
  );
});
